<template>
  <div class="container-page">
    <p class="title-inner">Рейтинги</p>
    <div class="mb-16">
      <ButtonComponent
        v-permission="permissions.import"
        mod="gradient-bg"
        buttonType="button"
        :disabled="importInProgress"
        @click="importExcel()"
      >
        {{ !importInProgress ? 'Загрузить из Excel' : 'Загрузка...' }}
      </ButtonComponent>
    </div>
    <div class="mb-4">
      <DefaultFilter :filterValue="filter" @loadFilter="onLoadFilter" />
    </div>
    <div v-if="isAccessAllowed">
      <div class="table-container mb-16 js-table-arrow">
        <table class="stockTable">
          <thead>
            <tr>
              <th>Страна</th>
              <th>Standard & Poor's</th>
              <th>Moody's</th>
              <th>Fitch</th>
              <th>Индекс человеческого развития</th>
              <th>Индекс человеческого капитала</th>
              <th>Глобальноый индекс конкурентоспособности</th>
            </tr>
            <tr>
              <th class="filter">
                <FilterInput
                  type="select2"
                  :select2Options="countries"
                  :select2Settings="select2Settings"
                  v-model="filter.countryId"
                  placeholder="(Все)"
                  v-on:valueChangedDebounced="changeFilter"
                />
              </th>
              <th class="filter">
                <FilterInput v-model="filter.standardAndPoor" v-on:valueChangedDebounced="changeFilter()" />
              </th>
              <th class="filter">
                <FilterInput v-model="filter.moody" v-on:valueChangedDebounced="changeFilter()" />
              </th>
              <th class="filter">
                <FilterInput v-model="filter.fitch" v-on:valueChangedDebounced="changeFilter()" />
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.hdiRankFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.hdiRankTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.humanCapitalIndexFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.humanCapitalIndexTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
              <th class="filter">
                <div class="range">
                  <FilterInput
                    type="number"
                    v-model="filter.globalCompetitivenessIndexFrom"
                    class="mr-2"
                    placeholder="от"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                  <FilterInput
                    type="number"
                    v-model="filter.globalCompetitivenessIndexTo"
                    placeholder="до"
                    v-on:valueChangedDebounced="changeFilter()"
                  />
                </div>
              </th>
            </tr>
          </thead>
          <tbody v-if="page.items?.length && !isLoading">
            <tr v-for="item in page.items" :key="item.id">
              <td>{{ item.country.nameFull || item.country.name }}</td>
              <td>{{ item.standardAndPoor }}</td>
              <td>{{ item.moody }}</td>
              <td>{{ item.fitch }}</td>
              <td>{{ item.hdiRank }}</td>
              <td>{{ item.humanCapitalIndex }}</td>
              <td>{{ item.globalCompetitivenessIndex }}</td>
            </tr>
          </tbody>
          <tbody v-else-if="isLoading">
            <tr>
              <td class="text-center" colspan="7">Загрузка...</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="text-center" colspan="7">Нет данных.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-if="page.pagesTotal > 1">
        <Pagination
          :pageCount="page.pagesTotal"
          :currentPage="page.pageNumber"
          @change="onPageChange($event, false)"
          @more="onPageChange($event, true)"
        />
      </div>
    </div>
    <section>
      <ModalComponent v-model="importModal" title="Импорт файла" @close="closeModal">
        <ImportModal @doimport="doImport" @close="closeModal" />
      </ModalComponent>
    </section>
  </div>
</template>

<script>
  import DefaultFilter from '@/common/components/DefaultFilter.vue';
  import ButtonComponent from '@/common/components/redesigned/ButtonComponent.vue';
  import Constants from '@/common/constants';
  import security from '@/common/mixins/security';
  import Utils from '@/common/utils';
  import { loadFilter, saveFilter } from '@/common/utils/filterManager';

  import ImportModal from '@/components/modals/ImportModal.vue';

  import FilterInput from '../../../common/components/FilterInput.vue';
  import ModalComponent from '../../../components/modals/ModalComponent.vue';
  import Api from '../api/index';

  const PERMISSIONS = {
    view: Constants.Permissions.CountryRatingView,
    import: Constants.Permissions.CountryRatingImport,
  };

  export default {
    name: 'CountryRatings',
    components: { FilterInput, ImportModal, ModalComponent, ButtonComponent, DefaultFilter },
    mixins: [security],
    data() {
      return {
        route: 'countryrating',
        page: {},
        request: {
          pageNumber: 1,
          pageSize: Constants.pageSize,
        },
        filter: {
          countryId: null,
          standardAndPoor: null,
          moody: null,
          fitch: null,
          hdiRankFrom: null,
          hdiRankTo: null,
          humanCapitalIndexFrom: null,
          humanCapitalIndexTo: null,
          globalCompetitivenessIndexFrom: null,
          globalCompetitivenessIndexTo: null,
        },
        countries: [],
        select2Settings: Constants.select2Settings,
        importInProgress: false,
        importModal: false,
        isLoading: false,
      };
    },
    computed: {
      permissions: () => PERMISSIONS,
    },
    created() {
      this.checkPermissionAndRedirect(PERMISSIONS.view).then((isAllowed) => {
        if (!isAllowed) return;

        loadFilter(this.$options.name).then((response) => {
          let filter = response;
          if (filter) {
            this.filter = filter;
          }
          this.loadDictionaries();
          this.loadPage();
        });
      });
    },
    methods: {
      loadPage(append) {
        this.isLoading = !append;
        const request = Object.assign(this.filter, this.request);
        Api.search(this.route, request)
          .then((response) => {
            if (!append) {
              this.page = {};
              this.$nextTick(() => {
                this.page = response.data;
              });
            } else if (response.data) {
              this.page.pageNumber = response.data.pageNumber;
              this.page.items = this.page.items.concat(response.data.items);
            }
            this.isLoading = false;
          })
          .catch(() => {
            this.isLoading = false;
          });
      },
      loadDictionaries() {
        Utils.searchNsi('countrylistwithdata', {})
          .then((response) => {
            this.countries = response.data.map((x) => {
              return { text: x.name, id: x.id };
            });
          })
          .catch((error) => {
            console.error(error?.response?.data);
          });
      },
      importExcel() {
        this.importModal = true;
      },
      doImport(file) {
        this.closeModal();
        this.importInProgress = true;
        Api.downloadFile(this.route, file)
          .then(() => {
            Constants.alert.fire('Импорт', 'Импорт успешно выполнен.', 'success');
            this.loadPage();
            this.importInProgress = false;
          })
          .catch((error) => {
            console.log(error?.response?.data);
            Constants.alert.fire('Импорт', 'Ошибка при импорте данных.', 'error');
            this.importInProgress = false;
          });
      },
      onPageChange(pageNumber, addToExist) {
        this.request.pageNumber = pageNumber;
        this.loadPage(addToExist);
      },
      changeFilter() {
        this.request.pageNumber = 1;
        saveFilter(this.$options.name, this.filter);
        this.loadPage(false);
      },
      closeModal() {
        this.importModal = false;
      },
      onLoadFilter(item) {
        this.filter = item;
        this.changeFilter();
      },
    },
  };
</script>

<style lang="scss">
  .filter {
    .range {
      display: flex;
      .input-group-wrapper {
        input {
          padding: 4px 8px;
          min-width: 80px;
          max-width: 80px;
        }
      }
    }
  }
</style>
